import React from "react";

import { useKeybanClient } from "~/provider";

import styles from "./KeybanInput.module.css";

type ContainerProps = Pick<
  React.HTMLProps<HTMLDivElement>,
  "className" | "style"
>;
type InputProps = Pick<React.HTMLProps<HTMLInputElement>, "type" | "inputMode">;

type BaseProps = ContainerProps & InputProps;

/**
 * Props for the KeybanInput component.
 * @example
 * ```tsx
 * const props: KeybanInputProps = {
 *   name: "email",
 *   type: "email",
 *   onFocus: () => console.log("focused"),
 *   inputStyles: { textAlign: "center" }
 * };
 * ```
 */
export type KeybanInputProps = BaseProps & {
  /**
   * The name attribute for the input, used to identify the input in form submissions.
   * This is required and should be unique within the form context.
   * @example "email", "password", "otp", "phone"
   */
  name: string;
  /**
   * Callback fired when the input receives focus.
   * Useful for managing form state or UI feedback.
   */
  onFocus?: () => void;
  /**
   * Callback fired when the input loses focus.
   * Useful for validation or form state management.
   */
  onBlur?: () => void;
  /**
   * Callback fired when the input value changes.
   * Note: The actual value is not accessible due to security isolation.
   */
  onInput?: () => void;
  /**
   * Callback fired when the input change event is triggered.
   * Note: The actual value is not accessible due to security isolation.
   */
  onChange?: () => void;
  /**
   * Styling options for the input element inside the iframe.
   * Limited to specific CSS properties for security reasons.
   * @example
   * ```tsx
   * inputStyles={{
   *   textAlign: "center",
   *   fontSize: "16px",
   *   color: "#333"
   * }}
   * ```
   */
  inputStyles?: Pick<
    { [K in keyof React.CSSProperties]: string },
    "colorScheme" | "backgroundColor" | "color" | "fontSize" | "textAlign"
  >;
};

/**
 * Ref interface for KeybanInput component.
 * Provides imperative methods to control the input.
 * @example
 * ```tsx
 * const inputRef = useRef<KeybanInputRef>(null);
 *
 * const handleButtonClick = () => {
 *   inputRef.current?.focus();
 * };
 *
 * <KeybanInput ref={inputRef} name="email" />
 * <button onClick={handleButtonClick}>Focus Input</button>
 * ```
 */
export interface KeybanInputRef {
  /**
   * Programmatically focus the input.
   * Useful for enhancing user experience and accessibility.
   */
  focus(): void;
}

/**
 * KeybanInput renders a secure iframe-based input component for the Keyban SDK client.
 * This component provides a secure way to handle sensitive user inputs like emails, phone numbers,
 * passwords, and OTP codes by isolating them in a sandboxed iframe.
 * @param props - KeybanInput props
 * @returns The iframe element for SDK client input.
 * @example
 * **Basic email input:**
 * ```tsx
 * <KeybanInput
 *   name="email"
 *   type="email"
 * />
 * ```
 * @example
 * **Phone number input:**
 * ```tsx
 * <KeybanInput
 *   name="phone"
 *   type="tel"
 * />
 * ```
 * @example
 * **OTP code input with custom styling:**
 * ```tsx
 * <KeybanInput
 *   name="otp"
 *   inputMode="numeric"
 *   inputStyles={{ textAlign: "center" }}
 * />
 * ```
 * @example
 * **Password input:**
 * ```tsx
 * <KeybanInput
 *   name="password"
 *   type="password"
 * />
 * ```
 * @example
 * **Integration with Material-UI TextField:**
 * ```tsx
 * <TextField
 *   label="Email"
 *   variant="outlined"
 *   fullWidth
 *   slots={{
 *     htmlInput: () => (
 *       <Box
 *         component={KeybanInput}
 *         name="email"
 *         type="email"
 *         sx={{ flexGrow: 1 }}
 *       />
 *     ),
 *   }}
 * />
 * ```
 * @example
 * **Integration with MUI Tel Input:**
 * ```tsx
 * const PhoneInput = () => (
 *   <Box
 *     component={KeybanInput}
 *     type="tel"
 *     name="phone"
 *     sx={{ flexGrow: 1, p: 2, pl: 0 }}
 *   />
 * );
 *
 * <MuiTelInput
 *   name="phone"
 *   defaultCountry="FR"
 *   fullWidth
 *   slots={{ htmlInput: PhoneInput }}
 * />
 * ```
 * @example
 * **With event handlers:**
 * ```tsx
 * <KeybanInput
 *   name="email"
 *   type="email"
 *   onFocus={() => console.log('Input focused')}
 *   onBlur={() => console.log('Input blurred')}
 *   onInput={() => console.log('Input changed')}
 * />
 * ```
 * @example
 * **Using with forwardRef for focus control:**
 * ```tsx
 * const inputRef = useRef<KeybanInputRef>(null);
 *
 * const handleFocus = () => {
 *   inputRef.current?.focus();
 * };
 *
 * <KeybanInput
 *   ref={inputRef}
 *   name="email"
 *   type="email"
 * />
 * ```
 * @remarks
 * **Common Use Cases:**
 * - **Authentication flows**: Email/password login, passwordless authentication
 * - **OTP verification**: SMS or email verification codes
 * - **Phone number input**: International phone numbers with country codes
 * - **Secure form inputs**: Any sensitive user data that needs to be handled securely
 *
 * **Integration Patterns:**
 * - **Standalone**: Direct usage as a form input
 * - **Material-UI**: Integration with TextField using slots
 * - **Custom components**: Wrapped in Box or other container components
 * - **Third-party libraries**: Integration with specialized input libraries like MUI Tel Input
 *
 * **Security Features:**
 * - **Iframe isolation**: Input is rendered in a sandboxed iframe for security
 * - **Cross-origin messaging**: Secure communication between iframe and parent
 * - **No direct DOM access**: Input values are not accessible from the parent context
 *
 * **Styling Options:**
 * - **Container styling**: Use `style` and `className` props for container appearance
 * - **Input styling**: Use `inputStyles` prop for iframe input appearance
 * - **Theme integration**: Automatically inherits color scheme from parent document
 * @see {@link KeybanInputProps} for all available props
 * @see {@link KeybanInputRef} for imperative API
 */
export const KeybanInput = React.forwardRef<KeybanInputRef, KeybanInputProps>(
  (props, ref) => {
    const {
      // Container props
      className,
      style,
      // Input props
      type,
      inputMode,
      // Keyban props
      name,
      onFocus,
      onBlur,
      onInput,
      onChange,
      inputStyles = {},
    } = props;

    const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(
      null,
    );

    const client = useKeybanClient();
    const iframeUrl = new URL("/sdk-client/input", client.apiUrl);

    if (type) iframeUrl.searchParams.set("type", type);
    if (inputMode) iframeUrl.searchParams.set("inputMode", inputMode);

    inputStyles.colorScheme ??= getComputedStyle(
      containerEl ?? document.querySelector("html")!,
    ).colorScheme;

    for (const [key, value] of Object.entries(inputStyles))
      iframeUrl.searchParams.set(key, value);

    const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

    const focus = React.useCallback(() => {
      iframeRef.current?.contentWindow?.postMessage("focus", iframeUrl.origin);
    }, [iframeUrl.origin]);

    React.useImperativeHandle(ref, () => ({ focus }), [focus]);

    React.useEffect(() => {
      const listener = (evt: MessageEvent) => {
        if (evt.source !== iframeRef.current?.contentWindow) return;

        switch (evt.data) {
          case "focus":
            setClassNames((arr) => [...arr, "focused"]);
            onFocus?.();
            break;

          case "blur":
            setClassNames((arr) => arr.filter((s) => s !== "focused"));
            onBlur?.();
            break;

          case "input":
            onInput?.();
            break;

          case "change":
            onChange?.();
            break;
        }
      };

      window.addEventListener("message", listener);

      return () => window.removeEventListener("message", listener);
    }, [onFocus, onBlur, onInput, onChange]);

    const [classNames, setClassNames] = React.useState<string[]>([]);

    return (
      <div
        onClick={focus}
        style={style}
        className={[styles["KeybanInput-container"], ...classNames, className]
          .filter(Boolean)
          .join(" ")}
        ref={setContainerEl}
      >
        <input
          tabIndex={-1}
          disabled
          readOnly
          className={styles["KeybanInput-input"]}
        />

        {containerEl && (
          <iframe
            ref={iframeRef}
            src={iframeUrl.toString()}
            name={name}
            className={styles["KeybanInput-iframe"]}
          />
        )}
      </div>
    );
  },
);
